import * as v from 'valibot';

import { ECaseCardStatus, ENOPOrganType } from '#shared';

import {
  ENOPCaseStatus,
  ENOPDonorType,
  ENOPRoutePlanningStatus,
} from '../enums';
import { CheckPoint } from '../types';

import { caseLogisticsResponseSchema } from './caseLogistics.schema';
import { hospitalSchema } from './hospital.schema';
import { maoTripSchema } from './maoTrip.schema';
import { organSchema } from './organ.schema';
import { pathLabelSchema } from './pathLabel.schema';
import { trialProgramSchema } from './trialProgram.schema';
import { fallbackWithIssue } from './valibot.helpers';

import type { ActiveCase } from '../types';

/**
 * `v.object` flags an absent key as an ERROR on a required entry, and skips it silently on an optional entry with no default.
 * A non-undefined default makes the inner schema's `@WARNING:` grade it, and a valid null stays silent.
 * This default is not a valid value, so the parsed output must not be stored.
 */
const ABSENT = '';

const transportNameSchema = v.picklist(
  ['groundOnly', 'airNonMao', 'airMao'],
  'Invalid transport type',
);

const checkPointSchema: v.GenericSchema<CheckPoint> = v.object({
  id: v.string('Checkpoint ID is missing'),
  point: v.number('Checkpoint point is missing'),
  type: v.optional(
    v.picklist(
      [
        'organ',
        'groundOnly',
        'airNonMao',
        'airMao',
        'donorOR',
        'donorOrAddress',
      ],
      'Invalid checkpoint type',
    ),
  ),
  hidden: v.boolean('Checkpoint visibility is missing'),
});

const activeCaseBaseSchema: v.GenericSchema<ActiveCase> = v.object({
  id: v.string('Case ID is missing'),
  unosDonorId: fallbackWithIssue(
    v.string(),
    '---',
    '@WARNING: UNOS Donor ID is missing',
  ),
  flightIds: fallbackWithIssue(
    v.array(v.string()),
    [],
    '@WARNING: Flight IDs is missing',
  ),
  label: fallbackWithIssue(
    v.string(),
    '---',
    '@WARNING: Case label is missing',
  ),
  donorType: v.enum(
    ENOPDonorType,
    issue => `@WARNING: Unexpected donor type ${issue.input}`,
  ),
  transplantCenterCode: fallbackWithIssue(
    v.string(),
    '---',
    '@WARNING: Transplant center code is missing',
  ),
  etaString: fallbackWithIssue(
    v.string(),
    '---',
    '@WARNING: ETA string is missing',
  ),
  etaUtc: fallbackWithIssue(
    v.nullable(v.string('ETA UTC is missing')),
    null,
    '@WARNING: ETA UTC is missing',
  ),
  donorORTimeString: fallbackWithIssue(
    v.string(),
    '---',
    '@WARNING: Donor OR time is missing',
  ),
  caseTimerEndString: fallbackWithIssue(
    v.nullable(v.string('Case timer end is missing')),
    null,
    '@WARNING: Case timer end is missing',
  ),
  showDonorOr: fallbackWithIssue(
    v.boolean(),
    false,
    '@WARNING: Show Donor OR flag is missing',
  ),
  showOrgan: fallbackWithIssue(
    v.boolean(),
    false,
    '@WARNING: Show Organ flag is missing',
  ),
  showTimer: fallbackWithIssue(
    v.boolean(),
    false,
    '@WARNING: Show Timer flag is missing',
  ),
  showReturnToBaseState: fallbackWithIssue(
    v.boolean(),
    false,
    '@WARNING: Show Return to Base flag is missing',
  ),
  donorORDateTimeUtc: fallbackWithIssue(
    v.nullable(v.string('Donor OR DateTime is missing')),
    null,
    '@WARNING: Donor OR DateTime is missing',
  ),
  showOrganOnCard: fallbackWithIssue(
    v.boolean(),
    false,
    '@WARNING: Show Organ on Card flag is missing',
  ),
  showTransportOnCard: fallbackWithIssue(
    v.boolean(),
    false,
    '@WARNING: Show Transport on Card flag is missing',
  ),
  showOnMap: fallbackWithIssue(
    v.boolean(),
    false,
    '@WARNING: Show on Map flag is missing',
  ),
  status: fallbackWithIssue(
    v.enum(
      ENOPCaseStatus,
      issue => `@WARNING: Unexpected case status ${issue.input}`,
    ),
    ENOPCaseStatus.Accepted,
    '@WARNING: Case status is missing',
  ),
  cardStatus: fallbackWithIssue(
    v.enum(
      ECaseCardStatus,
      issue => `@WARNING: Unexpected card status ${issue.input}`,
    ),
    ECaseCardStatus.Ok,
    '@WARNING: Card status is missing',
  ),
  organ: fallbackWithIssue(
    organSchema,
    {
      id: ENOPOrganType.Heart,
      name: ENOPOrganType.Heart,
      startPoint: 0,
    },
    '@WARNING: Organ is missing',
  ),
  pathLabels: fallbackWithIssue(
    v.array(pathLabelSchema, 'Path labels are missing'),
    [],
    '@WARNING: Path labels are missing',
  ),
  transport: fallbackWithIssue(
    v.nullable(transportNameSchema),
    null,
    '@WARNING: Transport wrong or missing',
  ),
  points: fallbackWithIssue(
    v.array(checkPointSchema, 'Checkpoints are missing'),
    [],
    '@WARNING: Checkpoints are missing',
  ),
  progress: fallbackWithIssue(
    v.number('Progress is missing'),
    0,
    '@WARNING: Progress is missing',
  ),
  mapProgress: fallbackWithIssue(
    v.number('Map progress is missing'),
    0,
    '@WARNING: Map progress is missing',
  ),
  donorHospital: fallbackWithIssue(
    v.nonNullish(hospitalSchema, 'Donor hospital is missing'),
    null,
    '@WARNING: Donor hospital is missing',
  ),
  recipientHospital: fallbackWithIssue(
    v.nullable(hospitalSchema),
    null,
    '@WARNING: TX Center is missing',
  ),
  trips: fallbackWithIssue(
    v.nullable(v.array(maoTripSchema, 'Invalid trips')),
    null,
    '@WARNING: Trips are missing',
  ),
  logistics: fallbackWithIssue(
    v.nullable(caseLogisticsResponseSchema),
    null,
    '@WARNING: Logistics data is missing or corrupted',
  ),
  trialProgram: fallbackWithIssue(
    v.nullable(trialProgramSchema),
    null,
    '@WARNING: Trial program is missing',
  ),
  serviceRegionId: fallbackWithIssue(
    v.optional(v.string('Invalid service region ID')),
    undefined,
    '@WARNING: Service region ID is missing or corrupted',
  ),
  routePlanningStatus: v.nullish(
    v.enum(
      ENOPRoutePlanningStatus,
      issue => `@WARNING: Unexpected route planning status ${issue.input}`,
    ),
  ),
  routePlanningStatusSince: v.nullish(
    v.pipe(
      v.string('@WARNING: Route planning status since is not a string'),
      v.isoTimestamp(
        '@WARNING: Route planning status since is not an ISO time',
      ),
    ),
  ),
  opoRequest: v.pipe(
    v.optional(v.unknown(), null),
    v.boolean('@WARNING: Opo request flag is missing or invalid'),
  ) as v.GenericSchema<ActiveCase['opoRequest']>,
  creationSide: v.pipe(
    v.optional(v.unknown(), ABSENT),
    v.nullable(
      v.picklist(
        ['Tmdx', 'Customer', 'Opo'],
        '@WARNING: Creation side is missing or invalid',
      ),
    ),
  ) as v.GenericSchema<ActiveCase['creationSide']>,
  primaryLogisticsManager: v.pipe(
    v.optional(v.unknown(), ABSENT),
    v.nullable(
      v.object(
        {
          id: v.string('@WARNING: Primary logistics manager id is invalid'),
          firstName: v.nullable(v.string('@WARNING: First name is invalid')),
          lastName: v.nullable(v.string('@WARNING: Last name is invalid')),
          email: v.nullable(v.string('@WARNING: Email is invalid')),
          phone: v.nullable(v.string('@WARNING: Phone is invalid')),
          role: v.string('@WARNING: Role is invalid'),
        },
        '@WARNING: Primary logistics manager is missing or invalid',
      ),
    ),
  ) as v.GenericSchema<ActiveCase['primaryLogisticsManager']>,
});

export const activeCaseSchema = v.pipe(
  activeCaseBaseSchema,
  v.check((data: ActiveCase) => {
    if (!data.trips || !data.etaUtc) {
      return true;
    }
    const aircraftToRecipientTrips = data.trips
      .flatMap(t => t.Legs)
      .filter(l => l.LegType.toLowerCase() === 'donor to recipient');
    if (aircraftToRecipientTrips.length === 0) {
      return true;
    }

    return (
      new Date(aircraftToRecipientTrips[0].OffBlocksUtc).getTime() <
      new Date(data.etaUtc).getTime()
    );
  }, '@WARNING: Flight Taken Off time is after ETA time'),
  v.check(data => {
    if (!data.trips || !data.etaUtc) {
      return true;
    }
    const aircraftToRecipientTrips = data.trips
      .flatMap(t => t.Legs)
      .filter(l => l.LegType.toLowerCase() === 'donor to recipient');
    if (aircraftToRecipientTrips.length === 0) {
      return true;
    }
    return (
      new Date(data.etaUtc).getTime() >
      new Date(
        aircraftToRecipientTrips[aircraftToRecipientTrips.length - 1]
          .OnBlocksUtc,
      ).getTime()
    );
  }, '@WARNING: Flight Arrival time is after ETA time'),
  v.check(data => {
    if (data.status === ENOPCaseStatus.ReturningToBase) {
      return true;
    }

    if (!data.etaUtc || !data.donorORDateTimeUtc) {
      return true;
    }

    return (
      new Date(data.etaUtc).getTime() >=
      new Date(data.donorORDateTimeUtc).getTime()
    );
  }, '@WARNING: ETA time is before Donor OR time'),
);
